import { createContext, useContext } from 'react';
import type { Conversation, MyMembership, User, Workspace } from '@/shared/api/schemas';

export interface WorkspaceValue {
  readonly workspace: Workspace;
  readonly me: MyMembership;
  readonly user: User;
  /** Начало адресов API пространства: /api/workspaces/{id}. */
  readonly api: string;
  /** Начало адресов страниц: /w/{id}. */
  readonly path: string;
}

export const WorkspaceContext = createContext<WorkspaceValue | null>(null);

export function useWorkspace(): WorkspaceValue {
  const value = useContext(WorkspaceContext);
  if (value === null) {
    throw new Error('useWorkspace вне пространства');
  }
  return value;
}

/**
 * Служебные пункты «Мои запросы» и «Дизайн-система» нужны владельцу продукта — Андрею Пьянкову; остальным их в меню не показываем
 * (демо-Андрей — чтобы они были видны и в демо).
 */
const PRODUCT_OWNERS: ReadonlySet<string> = new Set(['a.piankov@passwork.team', 'andrei@demo.local']);

export function isProductOwner(user: Pick<User, 'email'>): boolean {
  return PRODUCT_OWNERS.has(user.email.toLowerCase());
}

export function isAdmin(me: MyMembership): boolean {
  return me.role === 'owner' || me.role === 'admin';
}

/**
 * Доступ к людям: приглашения, отключение, отзыв доступа, единый вход, журнал. Как Role::canManageAccess на сервере.
 * ИТ-администратор сюда входит, а в isAdmin — нет: переписку, тариф и ключи он не видит.
 */
export function canManageAccess(me: MyMembership): boolean {
  return isAdmin(me) || me.role === 'it_admin';
}

/** Управляет каналом (архив, переименование, закрытие): администратор, создатель или владелец из списка участников — как ConversationService::canManage. */
export function canManageChannel(c: Pick<Conversation, 'createdById' | 'channelOwner'>, me: MyMembership, userId: string): boolean {
  return isAdmin(me) || c.createdById === userId || c.channelOwner;
}

/** Кто видит «Пригласить»: администратор зовёт сам, участник — заявкой, если она включена в пространстве; гость — нет. */
export function canInvite(me: MyMembership): boolean {
  return canManageAccess(me) || (me.role !== 'guest' && me.workspace.inviteRequests);
}
